import { Circle } from '@react-google-maps/api';
import { useCallback, useEffect, useRef } from 'react';

import { LngLat } from '../interfaces';

/** Editable circle: callbacks on drag end (center move) and radius change (resize). */
export function EditableCircle({
  center,
  radius,
  onEdit,
}: {
  center: LngLat;
  radius: number;
  onEdit: (center: LngLat, radius: number) => void;
}) {
  const onEditRef = useRef(onEdit);
  onEditRef.current = onEdit;
  const circleRef = useRef<google.maps.Circle | null>(null);
  const lastPropsRef = useRef({ center, radius });

  const notify = useCallback(() => {
    const circle = circleRef.current;
    if (!circle) return;
    const c = circle.getCenter();
    if (!c) return;
    const lat = c.lat();
    const lng = c.lng();
    const r = circle.getRadius();
    // Avoid feedback loop: radius_changed can fire when parent updates the radius prop.
    // Only notify when circle state actually differs from last props (user edit).
    const { center: lastCenter, radius: lastRadius } = lastPropsRef.current;
    const sameCenter =
      Math.abs(lastCenter.lat - lat) < 1e-9 && Math.abs(lastCenter.lng - lng) < 1e-9;
    const sameRadius = Math.abs(lastRadius - r) < 1e-6;
    if (sameCenter && sameRadius) return;
    lastPropsRef.current = { center: { lat, lng }, radius: r };
    onEditRef.current({ lat, lng }, r);
  }, []);

  useEffect(() => {
    lastPropsRef.current = { center, radius };
  }, [center, radius]);

  const onLoad = useCallback((circle: google.maps.Circle) => {
    circleRef.current = circle;
  }, []);

  return (
    <Circle
      key="editable-geofence-circle"
      center={center}
      radius={radius}
      draggable
      editable
      options={
        {
          fillColor: 'orange',
          fillOpacity: 0.35,
          strokeColor: 'orange',
          strokeOpacity: 0.8,
          strokeWeight: 2,
          zIndex: 2,
          suppressUndo: true, // Undocumented Google Maps API: hides "undo last edit" overlay
        } as google.maps.CircleOptions
      }
      onLoad={onLoad}
      onDragEnd={notify}
      onRadiusChanged={notify}
    />
  );
}
